import React, { memo, useEffect, useState, useMemo } from 'react';
import { Strategy } from 'src/api/types';
import { useColumns } from './useColumns';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty, TableSheetRmp } from 'src/components/GenericTable';
import { Spacer, TableSkeleton } from 'src/common';
import _, { isEqual } from 'lodash';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { AddRmpModal } from '../../../AddRmpModal';
import { useAuth } from 'src/state/auth';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import { SuggestModifiersChecker } from 'src/components/modals/comps';
import { useControlsQuery, useControlsRmpQuery } from 'src/graphql/_generated';

import { isQueryLoading } from 'src/graphql';

interface FromExistingProps {
  scenarioId: string;
  setChecked: (a: string[]) => void;
  isLoading: boolean;
  onSubmit: () => void;
  addRecommendedModifiers: boolean;
  setAddRecommendedModifiers: (a: boolean) => void;
}

export const FromExisting: React.FC<FromExistingProps> = memo(
  ({ scenarioId, setChecked, isLoading, onSubmit, addRecommendedModifiers, setAddRecommendedModifiers }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const { features, workspaceId } = useAuth();

    const { data, loading, networkStatus } = useControlsQuery();
    const isControlsLoading = isQueryLoading({ loading, data, networkStatus });

    const controls = _.orderBy(data?.controls, ['meta.createdAt'], ['desc']);

    const {
      data: { controls: controlsRmp = [] } = {},
      loading: controlsRmpLoading,
      networkStatus: controlsRmpNetworkStatus,
    } = useControlsRmpQuery({ variables: { workspaceId: workspaceId } });
    const isControlsRmpLoading = isQueryLoading({
      loading: controlsRmpLoading,
      data: controlsRmp,
      networkStatus: controlsRmpNetworkStatus,
    });

    const { columns, GTColumnSelectAnchorExported, checked } = useColumns({
      data: controls,
      controlsRmp,
      scenarioId,
    });

    useEffect(() => {
      setChecked(checked);
    }, [checked]);

    const isDisabledModSuggest = useMemo(() => {
      if (!checked.length) return true;
      return !controls
        .filter((el) => checked.includes(el.id))
        .some((el) => checkIsModifierStrategy(el.strategy as Strategy));
    }, [controls, checked]);

    useEffect(() => {
      if (isDisabledModSuggest && addRecommendedModifiers) {
        setAddRecommendedModifiers(false);
      }
    }, [isDisabledModSuggest, addRecommendedModifiers, setAddRecommendedModifiers]);

    const isAnyLoading = isControlsLoading || isControlsRmpLoading;

    return (
      <>
        {isAddModalOpen && <AddRmpModal onClose={() => setIsAddModalOpen(false)} />}
        {!isAnyLoading ? (
          <>
            <GenericTable
              tableId={TableIds.scenarioLinkExistingControl}
              data={controls}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              searchable
              expandContent={(data) => <TableSheetRmp data={data} />}
              itemHeight={50}
              rowDisabled={(row) =>
                controlsRmp.some(
                  (control) =>
                    control.id === row.id &&
                    control.riskManagementPlans.some((plan) => plan.scenario?.id === scenarioId),
                )
              }
              emptyRow={
                <GTRowEmpty
                  itemHeight={50}
                  text={genEmtyRowTextComponent({
                    onClick: () => setIsAddModalOpen(true),
                  })}
                />
              }
            />
            {features['app.ai.suggestModifiers'] && (
              <SuggestModifiersChecker
                isDisabledModSuggest={isDisabledModSuggest}
                addRecommendedModifiers={addRecommendedModifiers}
                setAddRecommendedModifiers={setAddRecommendedModifiers}
              />
            )}
            <Spacer $px={30} />
            <Button
              disabled={isLoading || !checked.length}
              onClick={() => {
                onSubmit();
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Add',
                  modal: 'Link controls to scenario modal',
                  tags: ['SCENARIO', 'RMP'],
                });
              }}
              primary
              css="width: 100%;"
              data-cy="submit-btn"
            >
              + ADD
            </Button>
          </>
        ) : (
          <TableSkeleton />
        )}
      </>
    );
  },
  isEqual,
);
